import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { StatusBadge } from 'src/components/common/StatusBadge';
import DataTable from 'src/components/ui/data-table';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useOrganizationUsers } from 'src/services/api/queries/organizationQueries';
import { OrganizationService } from 'src/services/api/OrganizationService';
import { useRegisterOrganizationUser } from 'src/services/api/mutations/organizationMutations';
import { AddOrgUserModal } from 'src/components/Modals/AddOrgUserModal';
import {
  Mail,
  Phone,
  Calendar,
  UserRoundPlus,
  MoreHorizontal,
  UserX,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';

const DEFAULT_PAGE_SIZE = 10;

const getInitials = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

const NameCell = ({ row }) => {
  const name = [
    row.original.first_name,
    row.original.middle_name,
    row.original.last_name,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className="text-[13px] font-[550] text-foreground-strong">
      {name}
    </span>
  );
};

const EmailCell = ({ value }) => (
  <div className="flex items-center gap-1.5">
    <Mail
      className="w-3.5 h-3.5 text-primary flex-shrink-0"
      strokeWidth={1.8}
    />
    <span className="text-[12.5px] text-[#2168B2] truncate">{value}</span>
  </div>
);

const PhoneCell = ({ value }) => {
  if (!value)
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <Phone
        className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"
        strokeWidth={1.8}
      />
      <span className="text-[12.5px] text-gray-500">{value}</span>
    </div>
  );
};

const ActiveStatusCell = ({ value }) => (
  <StatusBadge value={value ? 'ACTIVE' : 'INACTIVE'} />
);

const DateCell = ({ value }) => {
  if (!value)
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const formatted = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return (
    <div className="flex items-center gap-1.5">
      <Calendar
        className="w-3.5 h-3.5 text-muted-foreground"
        strokeWidth={1.8}
      />
      <span className="text-[12.5px] text-gray-500">{formatted}</span>
    </div>
  );
};

const ActionCell = ({ row, onToggleStatus, isToggling }) => {
  const isActive = row.original.is_active;
  const userId = row.original.id;
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleConfirmAction = () => {
    onToggleStatus(userId);
    setShowConfirmDialog(false);
  };

  if (!isActive) {
    return <span className="text-[12.5px] text-gray-300">—</span>;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity rounded-md hover:bg-muted"
          >
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => setShowConfirmDialog(true)}
            disabled={isToggling}
            className="text-destructive focus:text-destructive"
          >
            <UserX className="mr-2 h-4 w-4" />
            Deactivate
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <div className="flex flex-col items-center text-center gap-3 pt-2">
              <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle
                  className="w-6 h-6 text-red-500"
                  strokeWidth={2}
                />
              </div>
              <div>
                <DialogTitle className="text-[15px] font-semibold text-foreground-strong">
                  Deactivate User
                </DialogTitle>
                <DialogDescription className="text-[13px] text-gray-500 mt-1">
                  Are you sure you want to deactivate this user? They will lose
                  access immediately.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="flex justify-center gap-2 pt-2 pb-1">
            <Button
              type="button"
              variant="outline"
              className="min-w-[90px] text-[13px]"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isToggling}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="min-w-[90px] text-[13px]"
              onClick={handleConfirmAction}
              disabled={isToggling}
            >
              {isToggling ? 'Processing...' : 'Deactivate'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const getColumns = (onToggleStatus, isToggling) => [
  {
    key: 'first_name',
    header: 'Name',
    minWidth: '200px',
    render: (_v, row) => <NameCell row={{ original: row }} />,
  },
  {
    key: 'email',
    header: 'Email',
    minWidth: '220px',
    render: (v) => <EmailCell value={v} />,
  },
  {
    key: 'phone',
    header: 'Phone',
    width: '170px',
    minWidth: '140px',
    render: (v) => <PhoneCell value={v} />,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <ActiveStatusCell value={v} />,
  },
  {
    key: 'created_at',
    header: 'Created',
    width: '150px',
    minWidth: '130px',
    render: (v) => <DateCell value={v} />,
  },
  {
    key: 'actions',
    header: '',
    width: '70px',
    minWidth: '60px',
    render: (_v, row) => (
      <ActionCell
        row={{ original: row }}
        onToggleStatus={onToggleStatus}
        isToggling={isToggling}
      />
    ),
  },
];

const OrganizationUsersDetail = () => {
  const { toast } = useToast();
  const { organizationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationName = searchParams.get('name') || `ID: ${organizationId}`;
  const orgId = searchParams.get('orgId') || '';
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const registerOrganizationUserMutation = useRegisterOrganizationUser();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useOrganizationUsers(organizationId, currentPage, currentPageSize);

  const users = usersData?.data?.users || [];
  const totalItems = usersData?.data?.pagination?.total || 0;

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  const handleToggleStatus = async (userId) => {
    setIsToggling(true);
    try {
      await OrganizationService.deactivateUser(organizationId, userId);
      toast({
        title: 'Success',
        description: 'User deactivated successfully!',
      });
      refetch();
    } catch (deactivateError) {
      console.error('Error deactivating user:', deactivateError);
      toast({
        title: 'Error',
        description:
          deactivateError.response?.data?.message ||
          'Failed to deactivate user',
        variant: 'destructive',
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleSubmitUser = async (formData) => {
    if (!formData || !orgId) {
      toast({
        title: 'Error',
        description: 'Missing required data for user creation.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await registerOrganizationUserMutation.mutateAsync({
        org_id: orgId,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName || '',
        phone: formData.phone || '',
      });

      toast({
        title: 'Success',
        description: 'User added successfully!',
        variant: 'default',
      });
      setIsAddUserModalOpen(false);
      await refetch();
    } catch (submitError) {
      console.error('Error adding user:', submitError);
      toast({
        title: 'Error',
        description:
          submitError.response?.data?.message ||
          'An error occurred while adding the user. Please try again.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = getColumns(handleToggleStatus, isToggling);
  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Organization Users', href: '/organization-users' },
    {
      label: organizationName,
      href: `/organization-users/${organizationId}`,
    },
  ];

  const emptyState = (
    <div className="bg-card rounded-xl border border-gray-100 dark:border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Users className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No users found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        No users have been added to this organisation yet.
      </p>
      <button
        type="button"
        onClick={() => setIsAddUserModalOpen(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
        Add User
      </button>
    </div>
  );

  if (error) {
    return (
      <div>
        <PageHeader breadcrumbs={breadcrumbItems} />
        <div className="p-6">
          <div className="bg-card rounded-xl border border-gray-100 dark:border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mb-5">
              <AlertTriangle
                className="w-7 h-7 text-red-400"
                strokeWidth={1.5}
              />
            </div>
            <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
              Failed to load users
            </p>
            <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
              {error.message || 'An error occurred while loading the users.'}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <button
            type="button"
            onClick={() => setIsAddUserModalOpen(true)}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add User
          </button>
        }
      />

      <PageContainer className="space-y-5">
        {organizationName && (
          <div className="flex items-center gap-3.5 px-4 py-3.5 bg-card border border-gray-100 dark:border-border rounded-[10px] shadow-sm">
            <div className="w-10 h-10 min-w-[40px] rounded-[10px] bg-brand-bg-subtle flex items-center justify-center text-[14px] font-bold text-[#2168B2] tracking-tight flex-shrink-0">
              {getInitials(organizationName)}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-[14px] font-[650] text-foreground-strong leading-snug truncate text-left">
                {organizationName}
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5 text-left">
                {orgId || organizationId}
              </p>
            </div>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold tracking-wide uppercase bg-brand-bg-subtle text-[#2168B2] whitespace-nowrap flex-shrink-0">
              Organisation
            </span>
          </div>
        )}

        <DataTable
          className="rounded-xl overflow-hidden"
          data={users}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-default'
          }
        />
      </PageContainer>

      <AddOrgUserModal
        isOpen={isAddUserModalOpen}
        onSubmit={handleSubmitUser}
        onClose={() => setIsAddUserModalOpen(false)}
        loading={registerOrganizationUserMutation.isPending}
        organizationName={organizationName}
      />
    </div>
  );
};

export default OrganizationUsersDetail;
